import { store } from "../../store";
import { Links } from "./base";

interface AccessReportPrincipleData {
  permission: string;
  policy: string;
  granted: string;
}

interface AccessReportPermissionBinding {
  grantedPermissions: string[];
  grantingPolicyLink: string;
  match: "all" | "link" | "query" | string;
  principalLink: string;
}

interface AccessReportPermission {
  name: string;
  description: string;
  bindings: AccessReportPermissionBinding[];
}

export interface AccessReportTableItem {
  permission: string;
  principal: {
    name: string;
    link: string;
  };
  granted: string;
}

const builtinPrincipals = ["/superusers", "/viewers", "/controlplane", "/-allmembers"];

const builtinPolicies = [
  "/superusers-agent",
  "/superusers-auditctx",
  "/superusers-cloudaccount",
  "/superusers-domain",
  "/superusers-group",
  "/superusers-gvc",
  "/superusers-identity",
  "/superusers-image",
  "/superusers-ipset",
  "/superusers-location",
  "/superusers-mk8s",
  "/superusers-org",
  "/superusers-policy",
  "/superusers-quota",
  "/superusers-secret",
  "/superusers-serviceaccount",
  "/superusers-user",
  "/superusers-volumeset",
  "/superusers-workload",
  "/viewers-agent",
  "/viewers-auditctx",
  "/viewers-cloudaccount",
  "/viewers-domain",
  "/viewers-group",
  "/viewers-gvc",
  "/viewers-identity",
  "/viewers-image",
  "/viewers-ipset",
  "/viewers-location",
  "/viewers-mk8s",
  "/viewers-org",
  "/viewers-policy",
  "/viewers-quota",
  "/viewers-secret",
  "/viewers-serviceaccount",
  "/viewers-user",
  "/viewers-volumeset",
  "/viewers-workload",
];

function isBuiltinPrincipal(value: string) {
  for (let principal of builtinPrincipals) {
    if (value.includes(principal)) {
      return true;
    }
  }
  return false;
}

function isBuiltinPolicy(value: string) {
  for (let policy of builtinPolicies) {
    if (value.includes(policy)) {
      return true;
    }
  }
  return false;
}

export interface AccessReport {
  kind: "accessreport";
  permissions: AccessReportPermission[];
  created: string;
  links: Links;
}

export const accessReportHelpers = {
  processByPermissionWithoutBuiltin(report: AccessReport) {
    const permissions: AccessReportPermission[] = [];
    for (let permission of report.permissions) {
      const bindings: AccessReportPermissionBinding[] = [];
      for (let binding of permission.bindings) {
        if (isBuiltinPolicy(binding.grantingPolicyLink)) {
          continue;
        }
        if (isBuiltinPrincipal(binding.principalLink)) {
          continue;
        }
        bindings.push(binding);
      }
      if (bindings.length < 1) {
        continue;
      }
      permissions.push({ bindings, name: permission.name, description: permission.description });
    }
    return permissions;
  },
  processByPrincipal(report: AccessReport) {
    const principals: { [_: string]: AccessReportPrincipleData[] } = {};
    report.permissions.forEach((permission) => {
      // permission policy grant
      permission.bindings.forEach((binding) => {
        if (!principals[binding.principalLink]) {
          principals[binding.principalLink] = [];
        }
        principals[binding.principalLink].push({
          permission: permission.name,
          policy: binding.grantingPolicyLink,
          granted: binding.grantedPermissions.join(", "),
        });
      });
    });

    const { org, gvc } = store.getState().context;
    const userLink = `/org/${org}/user/`;
    const groupLink = `/org/${org}/group/`;
    const serviceaccountLink = `/org/${org}/serviceaccount/`;
    const identityLink = `/org/${org}/gvc/${gvc}/identity/`;

    const users: { [_: string]: AccessReportPrincipleData[] } = {};
    const groups: { [_: string]: AccessReportPrincipleData[] } = {};
    const serviceaccounts: { [_: string]: AccessReportPrincipleData[] } = {};
    const identities: { [_: string]: AccessReportPrincipleData[] } = {};
    Object.keys(principals).forEach((principal) => {
      const data = principals[principal];
      if (principal.startsWith(userLink)) {
        users[principal] = data;
      } else if (principal.startsWith(groupLink)) {
        groups[principal] = data;
      } else if (principal.startsWith(serviceaccountLink)) {
        serviceaccounts[principal] = data;
      } else if (principal.startsWith(identityLink)) {
        identities[principal] = data;
      }
    });

    return { users, groups, serviceaccounts, identities };
  },
  processByPrincipalWithoutBuiltin(report: AccessReport) {
    const principals: { [_: string]: AccessReportPrincipleData[] } = {};
    for (let permission of report.permissions) {
      for (let binding of permission.bindings) {
        if (isBuiltinPrincipal(binding.principalLink) || isBuiltinPolicy(binding.grantingPolicyLink)) {
          continue;
        }
        if (!principals[binding.principalLink]) {
          principals[binding.principalLink] = [];
        }
        principals[binding.principalLink].push({
          permission: permission.name,
          policy: binding.grantingPolicyLink,
          granted: binding.grantedPermissions.join(", "),
        });
      }
    }
    const { org, gvc } = store.getState().context;
    const userLink = `/org/${org}/user/`;
    const groupLink = `/org/${org}/group/`;
    const serviceaccountLink = `/org/${org}/serviceaccount/`;
    const identityLink = `/org/${org}/gvc/${gvc}/identity/`;

    const users: { [_: string]: AccessReportPrincipleData[] } = {};
    const groups: { [_: string]: AccessReportPrincipleData[] } = {};
    const serviceaccounts: { [_: string]: AccessReportPrincipleData[] } = {};
    const identities: { [_: string]: AccessReportPrincipleData[] } = {};
    Object.keys(principals).forEach((principal) => {
      const data = principals[principal];
      if (principal.startsWith(userLink)) {
        users[principal] = data;
      } else if (principal.startsWith(groupLink)) {
        groups[principal] = data;
      } else if (principal.startsWith(serviceaccountLink)) {
        serviceaccounts[principal] = data;
      } else if (principal.startsWith(identityLink)) {
        identities[principal] = data;
      }
    });

    return { users, groups, serviceaccounts, identities };
  },
  getHasCustomByPrincipal(report: AccessReport) {
    const data = this.processByPrincipalWithoutBuiltin(report);
    return (
      Object.keys(data.users).length > 0 ||
      Object.keys(data.groups).length > 0 ||
      Object.keys(data.serviceaccounts).length > 0 ||
      Object.keys(data.identities).length > 0
    );
  },
  getHasCustomByPermission(report: AccessReport) {
    return this.processByPermissionWithoutBuiltin(report).length > 0;
  },
  getByPrincipal(report: AccessReport, hideBuiltin: boolean) {
    if (hideBuiltin) {
      return this.processByPrincipalWithoutBuiltin(report);
    }
    return this.processByPrincipal(report);
  },
  getByPermission(report: AccessReport, hideBuiltin: boolean) {
    if (hideBuiltin) {
      return this.processByPermissionWithoutBuiltin(report);
    }
    return report.permissions;
  },
};
